import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { t } from "@shared/i18n";

export type Repeat = "none" | "daily" | "weekdays" | "weekly" | "monthly";
export type Reminder = { id: string; text: string; remindAt: string; repeat: Repeat; email: boolean; href: string | null; source: string; lastSentAt: string | null; doneAt: string | null; createdAt: string };
export const REPEAT_LABELS: Record<Repeat, string> = { none: t("Une fois"), daily: t("Tous les jours"), weekdays: t("Jours ouvrés"), weekly: t("Chaque semaine"), monthly: t("Chaque mois") };
export const remindersKey = ["reminders"] as const;

export function useReminders(opts: { refetchInterval?: number } = {}) {
  return useQuery({ queryKey: remindersKey, queryFn: async () => (await api<{ data: Reminder[] }>("/api/reminders")).data, refetchInterval: opts.refetchInterval });
}

export function useReminderMutations() {
  const qc = useQueryClient();
  const done = () => Promise.all([qc.invalidateQueries({ queryKey: remindersKey }), qc.invalidateQueries({ queryKey: ["notifications"] })]);
  return {
    create: useMutation({ mutationFn: (b: { text: string; remindAt: string; repeat: Repeat; email: boolean }) => api<{ data: Reminder }>("/api/reminders", { method: "POST", json: b }), onSuccess: done }),
    update: useMutation({ mutationFn: ({ id, ...b }: { id: string } & Partial<{ text: string; remindAt: string; repeat: Repeat; email: boolean; done: boolean }>) => api(`/api/reminders/${id}`, { method: "PATCH", json: b }), onSuccess: done }),
    snooze: useMutation({ mutationFn: ({ id, minutes }: { id: string; minutes: number }) => api(`/api/reminders/${id}/snooze`, { method: "POST", json: { minutes } }), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: string) => api(`/api/reminders/${id}`, { method: "DELETE" }), onSuccess: done }),
  };
}
